import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/modules/shared/components/ui/card'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/modules/shared/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/modules/shared/components/ui/select'
import { useMemo, useState } from 'react'
import type { ComparativeSeller } from '../../types/comparative-report.type'
import {
  type ComparativeMetric,
  type ComparativeOrder,
  getComparativeValue,
  sortComparativeSellers,
} from './comparative-chart.utils'

const money = (value: number) =>
  value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
const percentage = (value: number) =>
  `${value.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`
const compactMoney = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  })

export function ComparativeSalesChart({
  sellers,
}: { sellers: ComparativeSeller[] }) {
  const [metric, setMetric] = useState<ComparativeMetric>('percentage')
  const [order, setOrder] = useState<ComparativeOrder>('descending')
  const sorted = useMemo(
    () => sortComparativeSellers(sellers, metric, order),
    [sellers, metric, order],
  )
  const values = sorted.map(
    (seller) => getComparativeValue(seller, metric) ?? 0,
  )
  const minimum = Math.min(0, ...values)
  const maximum = Math.max(metric === 'percentage' ? 100 : 1, ...values)
  const range = maximum - minimum
  const position = (value: number) => ((value - minimum) / range) * 100
  const format = metric === 'percentage' ? percentage : compactMoney

  return (
    <Card>
      <CardHeader className="space-y-3">
        <CardTitle className="text-lg">
          {metric === 'percentage'
            ? 'Atingimento da meta por vendedor'
            : 'Valor vendido por vendedor'}
        </CardTitle>
        <div className="flex flex-wrap gap-3">
          <div className="min-w-[170px] flex-1 space-y-1">
            <label htmlFor="comparative-metric" className="text-sm font-medium">
              Exibir
            </label>
            <Select
              value={metric}
              onValueChange={(value: ComparativeMetric) => setMetric(value)}
            >
              <SelectTrigger id="comparative-metric">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="percentage">Atingimento (%)</SelectItem>
                <SelectItem value="sales">Valor vendido (R$)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[170px] flex-1 space-y-1">
            <label htmlFor="comparative-order" className="text-sm font-medium">
              Ordenar
            </label>
            <Select
              value={order}
              onValueChange={(value: ComparativeOrder) => setOrder(value)}
            >
              <SelectTrigger id="comparative-order">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="descending">Maior → menor</SelectItem>
                <SelectItem value="ascending">Menor → maior</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <p className="text-xs text-muted-foreground">
          Toque ou clique em um vendedor para ver os detalhes. Valores completos
          disponíveis nos detalhes.
        </p>
        <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
          <span>
            <span className="mr-1 inline-block h-2 w-2 rounded bg-emerald-600" />
            Meta atingida
          </span>
          <span>
            <span className="mr-1 inline-block h-2 w-2 rounded bg-blue-600" />
            Abaixo da meta
          </span>
          <span>
            <span className="mr-1 inline-block h-2 w-2 rounded bg-slate-400" />
            Sem cota
          </span>
          {metric === 'percentage' && (
            <span>Linha tracejada: meta de 100%</span>
          )}
        </div>
      </CardHeader>
      <CardContent className="px-3 pb-4 sm:px-6">
        {sellers.length === 0 ? (
          <p role="status" className="py-8 text-center text-muted-foreground">
            Selecione pelo menos um vendedor.
          </p>
        ) : (
          <>
            <div
              className="grid grid-cols-[100px_minmax(0,1fr)_80px] gap-2 pb-2 text-[10px] text-muted-foreground sm:grid-cols-[200px_minmax(0,1fr)_100px]"
              aria-hidden="true"
            >
              <span>Vendedor</span>
              <div className="flex justify-between">
                <span>{format(minimum)}</span>
                <span>{format(maximum)}</span>
              </div>
              <span className="text-right">
                {metric === 'percentage' ? 'Atingimento' : 'Vendas'}
              </span>
            </div>
            <div
              className="max-h-[480px] overflow-y-auto"
              role="group"
              aria-label="Comparação de vendedores"
            >
              {sorted.map((seller) => {
                const value = getComparativeValue(seller, metric)
                const reached = seller.valorCota > 0 && seller.porc >= 100
                const color =
                  seller.valorCota <= 0
                    ? 'bg-slate-400'
                    : reached
                      ? 'bg-emerald-600'
                      : 'bg-blue-600'
                return (
                  <Popover key={seller.fkVendedor}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="grid min-h-12 w-full grid-cols-[100px_minmax(0,1fr)_80px] items-center gap-2 rounded border-b px-0 text-left hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary sm:grid-cols-[200px_minmax(0,1fr)_100px]"
                        aria-label={`${seller.nomeVendedor}: ${value === null ? 'Sem cota' : metric === 'sales' ? money(value) : percentage(value)}. Ver detalhes`}
                      >
                        <span
                          className="truncate text-xs"
                          title={seller.nomeVendedor}
                        >
                          {seller.nomeVendedor}
                        </span>
                        <span
                          className="relative block h-12"
                          aria-hidden="true"
                        >
                          <span
                            className="absolute inset-y-0 border-l border-border"
                            style={{ left: `${position(0)}%` }}
                          />
                          {value !== null && (
                            <span
                              className={`absolute top-4 h-4 rounded-sm ${color}`}
                              style={{
                                left: `${position(Math.min(0, value))}%`,
                                width: `${(Math.abs(value) / range) * 100}%`,
                              }}
                            />
                          )}
                          {metric === 'percentage' && (
                            <span
                              className="absolute inset-y-0 border-l border-dashed border-slate-500"
                              style={{ left: `${position(100)}%` }}
                            />
                          )}
                        </span>
                        <span className="text-right text-xs tabular-nums">
                          {value === null ? 'Sem cota' : format(value)}
                        </span>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="max-w-[calc(100vw-2rem)] text-sm">
                      <p className="mb-3 font-semibold">
                        {seller.nomeVendedor}
                      </p>
                      <dl className="space-y-2">
                        <div className="flex justify-between gap-3">
                          <dt>Vendas</dt>
                          <dd>{money(seller.valorVenda)}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt>Cota</dt>
                          <dd>{money(seller.valorCota)}</dd>
                        </div>
                        <div className="flex justify-between gap-3">
                          <dt>Atingimento</dt>
                          <dd>
                            {seller.valorCota > 0
                              ? percentage(seller.porc)
                              : 'Sem cota'}
                          </dd>
                        </div>
                        {seller.valorCota > 0 && (
                          <div className="flex justify-between gap-3">
                            <dt>
                              {seller.valorFalta < 0
                                ? 'Acima da meta'
                                : 'Falta para a meta'}
                            </dt>
                            <dd>{money(Math.abs(seller.valorFalta))}</dd>
                          </div>
                        )}
                        <div className="flex justify-between gap-3">
                          <dt>Quantidade de vendas</dt>
                          <dd>
                            {seller.quantidadeVendas.toLocaleString('pt-BR')}
                          </dd>
                        </div>
                      </dl>
                    </PopoverContent>
                  </Popover>
                )
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
